import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable"
import { useCursor } from "@/contexts/cursor"
import { useZeroFlowState } from "@/hooks/project/use-zero-flow-state"
import { usePlatform } from "@/platform/context"
import { useAuth } from "@/providers/auth"
import type { ProjectByIdResult } from "@/types/zero"
import { useEffect } from "react"
import { useLocation } from "react-router"
import { EmptyState } from "./EmptyState"
import { NodeEditor } from "./node-editor"
import { ProjectChatSidebar } from "./ProjectChatSidebar"

export const Workspace = ({ project }: { project: ProjectByIdResult }) => {
	const { nodes, edges, onNodesChange, onEdgesChange } =
		useZeroFlowState(project)
	const { user } = useAuth()
	const platform = usePlatform()

	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)
	const isOriginShell =
		platform.kind === "electron" || !!searchParams.get("origin")

	// Cursor sync
	const { connect: connectCursor, disconnect: disconnectCursor } = useCursor()

	// Connect to cursor sync when project loads
	useEffect(() => {
		if (project?.id && user?.id) {
			connectCursor(project.id)
		}
		return () => {
			disconnectCursor()
		}
	}, [project?.id, user?.id, connectCursor, disconnectCursor])

	return (
		<div
			className={`flex flex-col text-foreground ${isOriginShell ? "origin-project-workspace h-full" : "h-screen bg-background"}`}
		>
			<main className="flex-1 overflow-hidden">
				<ResizablePanelGroup orientation="horizontal" className="h-full">
					{platform.kind === "electron" ? (
						<>
							<ResizablePanel
								id="project-assistant"
								defaultSize="30%"
								minSize="300px"
								maxSize="55%"
							>
								{project?.id ? (
									<ProjectChatSidebar projectID={project.id} />
								) : null}
							</ResizablePanel>
							<ResizableHandle withHandle />
						</>
					) : null}
					<ResizablePanel
						id="project-main"
						defaultSize={platform.kind === "electron" ? "70%" : "100%"}
						minSize="360px"
					>
						<div className="relative h-full w-full bg-gray-100">
							<EmptyState />
							<NodeEditor
								nodes={nodes}
								edges={edges}
								onNodesChange={onNodesChange}
								onEdgesChange={onEdgesChange}
							/>
						</div>
					</ResizablePanel>
				</ResizablePanelGroup>
			</main>
		</div>
	)
}
